/**
 * Internationalization Configuration
 *
 * Defines supported locales and country-to-locale mappings for geolocation-based detection.
 * Default locale (en) has no URL prefix; others use /{locale}/path format.
 */

export const locales = [
  'en',
  'de',
  'el',
  'es',
  'fr',
  'hu',
  'id',
  'it',
  'ja',
  'nl',
  'pl',
  'pt',
  'pt-br',
  'ro',
  'ru',
  'tr',
] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

/**
 * Map ISO country codes to locales (used with Vercel geolocation headers)
 *
 * Countries not in this map default to 'en'.
 */
export const countryToLocale: Record<string, Locale> = {
  // German-speaking
  DE: 'de',
  AT: 'de',
  CH: 'de',
  LI: 'de',

  // Greek
  GR: 'el',
  CY: 'el',

  // Spanish-speaking
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CO: 'es',
  CL: 'es',
  PE: 'es',
  VE: 'es',
  EC: 'es',
  GT: 'es',
  CU: 'es',
  BO: 'es',
  DO: 'es',
  HN: 'es',
  PY: 'es',
  SV: 'es',
  NI: 'es',
  CR: 'es',
  PA: 'es',
  UY: 'es',

  // French-speaking
  FR: 'fr',
  BE: 'fr',
  MC: 'fr',
  LU: 'fr',

  // Hungarian
  HU: 'hu',

  // Indonesian
  ID: 'id',

  // Italian
  IT: 'it',

  // Japanese
  JP: 'ja',

  // Dutch-speaking
  NL: 'nl',

  // Polish
  PL: 'pl',

  // Portuguese
  PT: 'pt',
  BR: 'pt-br',

  // Romanian
  RO: 'ro',

  // Russian-speaking
  RU: 'ru',

  // Turkish
  TR: 'tr',
};

export { countryToCurrency, defaultCurrency, getCurrencyFromCountry } from '@impact/types';

/**
 * Map currency codes to a representative country code (inverse of countryToCurrency).
 * Used to derive an Adyen-compatible country when geo-detection is unavailable.
 */
export const currencyToCountry: Record<string, string> = {
  EUR: 'NL', // Adyen HQ, best EU payment method coverage
  GBP: 'GB',
  AUD: 'AU',
  NZD: 'NZ',
  CAD: 'CA',
  JPY: 'JP',
  CHF: 'CH',
  USD: 'US',
};

/**
 * Get a representative country code from a currency code.
 * Fallback chain: currency lookup → 'US' (platform default).
 */
export function getCountryFromCurrency(currencyCode: string | null | undefined): string {
  if (!currencyCode) return 'US';
  return currencyToCountry[currencyCode.toUpperCase()] ?? 'US';
}

/**
 * Check if a string is a valid locale
 */
export function isValidLocale(value: string): value is Locale {
  return locales.includes(value as Locale);
}

/**
 * Get locale from country code with fallback to default
 */
export function getLocaleFromCountry(countryCode: string | null | undefined): Locale {
  if (!countryCode) return defaultLocale;
  return countryToLocale[countryCode.toUpperCase()] ?? defaultLocale;
}

/**
 * Parse the Accept-Language header and return the best matching supported locale.
 *
 * Respects quality weights (e.g., "de-DE,de;q=0.9,en;q=0.8") and tries exact
 * match first (e.g., "pt-br"), then base language (e.g., "de-DE" → "de").
 *
 * Returns null if no supported locale matches (caller falls back to default).
 */
export function getLocaleFromAcceptLanguage(header: string | null): Locale | null {
  if (!header) return null;

  const parsed = header
    .split(',')
    .map((entry) => {
      const [code, q] = entry.trim().split(';q=');
      return { lang: code.trim().toLowerCase(), quality: q ? parseFloat(q) : 1.0 };
    })
    .sort((a, b) => b.quality - a.quality);

  for (const { lang } of parsed) {
    if (isValidLocale(lang)) return lang;
    const base = lang.split('-')[0];
    if (isValidLocale(base)) return base;
  }

  return null;
}

/**
 * Extract locale from URL path
 *
 * @returns tuple of [locale, pathWithoutLocale]
 * @example
 *   extractLocaleFromPath('/de/events') → ['de', '/events']
 *   extractLocaleFromPath('/events') → ['en', '/events']
 *   extractLocaleFromPath('/') → ['en', '/']
 */
export function extractLocaleFromPath(pathname: string): [Locale, string] {
  const localePattern = new RegExp(
    `^/(${locales.filter((l) => l !== defaultLocale).join('|')})(/|$)`,
  );
  const match = pathname.match(localePattern);

  if (match) {
    const locale = match[1] as Locale;
    const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/';
    return [locale, pathWithoutLocale];
  }

  return [defaultLocale, pathname];
}

/**
 * Build a locale-prefixed path
 *
 * @example
 *   buildLocalePath('de', '/events') → '/de/events'
 *   buildLocalePath('en', '/events') → '/events'
 */
export function buildLocalePath(locale: Locale, pathname: string): string {
  if (locale === defaultLocale) {
    return pathname;
  }
  return `/${locale}${pathname === '/' ? '' : pathname}`;
}

/**
 * Strip the internal /tenants/{slug} prefix from a rewritten pathname.
 *
 * Next.js `usePathname()` returns the rewritten path when middleware uses
 * `NextResponse.rewrite()`. Public tenant pages are rewritten from
 * `/events` → `/tenants/dgr/events`, so we need to reverse this to get
 * the user-facing path for locale extraction and link building.
 *
 * @example
 *   stripTenantPrefix('/tenants/dgr') → '/'
 *   stripTenantPrefix('/tenants/dgr/de/events') → '/de/events'
 *   stripTenantPrefix('/events') → '/events'  (no-op if no prefix)
 */
export function stripTenantPrefix(pathname: string): string {
  const match = pathname.match(/^\/tenants\/[^/]+(\/.*)?$/);
  if (match) {
    return match[1] || '/';
  }
  return pathname;
}

/**
 * Map locale to a suggested default currency.
 * Used when switching language to auto-suggest a matching currency.
 */
export const localeToCurrency: Record<Locale, string> = {
  en: 'USD',
  de: 'EUR',
  el: 'EUR',
  es: 'EUR',
  fr: 'EUR',
  hu: 'HUF',
  id: 'IDR',
  it: 'EUR',
  ja: 'JPY',
  nl: 'EUR',
  pl: 'PLN',
  pt: 'EUR',
  'pt-br': 'USD',
  ro: 'RON',
  ru: 'USD',
  tr: 'TRY',
};
